var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as React from 'react';
import Select, { components, createFilter, } from 'react-select';
import { isStringArray } from '../../utils/array';
import { CheckboxDropdownItem } from './CheckboxDropdownItem/CheckboxDropdownItem';
import { DropdownIcon } from './DropdownIcon/DropdownIcon';
import { MultiValueRemoveIcon } from './MultiValueRemoveIcon/MultiValueRemoveIcon';
import { getAmountOfOptionsThatFitInContainer } from './utils';


// extending react-select props interface
// related question:  https://stackoverflow.com/questions/66348283/to-extend-the-react-select-interface-property-in-typescript
function SelectWithCustomProps(props) {
    return React.createElement(Select, __assign({}, props));
}
var MultiValueRemove = function (props) { return (React.createElement(components.MultiValueRemove, __assign({}, props),
    React.createElement(MultiValueRemoveIcon, null))); };
var OptionMultiple = function (props) {
    var isSelected = props.isSelected, label = props.label, children = props.data.children;
    return (React.createElement(components.Option, __assign({}, props),
        React.createElement(CheckboxDropdownItem, { checked: isSelected }),
        React.createElement("label", null, label),
        children));
};
var MenuList = function (props) { return (React.createElement(components.MenuList, __assign({}, props),
    props.customChildren,
    props.children)); };
var MultiValueLabel = function (props) {
    var name = props.data.name;
    return (React.createElement(components.MultiValueLabel, __assign({}, props), name));
};
var MenuListWithSelectAllOption = function (props) {
    var allOptionsSelected = props.allOptionsSelected, selectAllLabel = props.selectAllLabel, onSelectAllOptions = props.onSelectAllOptions, onUnselectAllOptions = props.onUnselectAllOptions, customChildren = props.customChildren, children = props.children;
    var handleOptionClick = React.useCallback(function () {
        if (allOptionsSelected) {
            onUnselectAllOptions();
            return;
        }
        onSelectAllOptions();
    }, []);
    return (React.createElement(components.MenuList, __assign({}, props),
        React.createElement("div", { className: "pluggy-dropdown-menu" },
            React.createElement("button", { className: "dropdown__option", onClick: handleOptionClick, type: 'button' },
                React.createElement(CheckboxDropdownItem, { checked: allOptionsSelected }),
                React.createElement("label", null, selectAllLabel)),
            customChildren,
            children)));
};
var OptionSingle = function (props) {
    var label = props.label, children = props.data.children;
    return (React.createElement(components.Option, __assign({}, props),
        React.createElement("label", null, label),
        React.createElement("div", { className: 'option-children' }, children)));
};
// icon for dropdown
var DropdownIndicator = function (props) { return (React.createElement(components.DropdownIndicator, __assign({}, props),
    React.createElement(DropdownIcon, null))); };
var IndicatorsContainer = function (props) { return React.createElement(components.IndicatorsContainer, __assign({}, props)); };
var filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
};
var ValueContainer = React.memo(function (props) {
    var _a = props.selectProps, allOptionsSelectedLabel = _a.allOptionsSelectedLabel, withOverflowCount = _a.withOverflowCount;
    // if it's a single select, render default ValueContainer
    if (!props.isMulti) {
        return React.createElement(components.ValueContainer, __assign({}, props));
    }
    var valueContainerRef = React.useRef(null);
    var _b = React.useState(0), valueContainerWidth = _b[0], setValueContainerWidth = _b[1];
    React.useEffect(function () {
        var _a;
        setValueContainerWidth(((_a = valueContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0);
    }, []);
    var selectedOptions = __spreadArrays(props.getValue());
    var optionsThatFitInsideTheContainer = getAmountOfOptionsThatFitInContainer(selectedOptions, valueContainerWidth);
    var _c = props.children, optionsElements = _c[0], otherChildren = _c[1];
    var displayOptionElements = (optionsElements === null || optionsElements === void 0 ? void 0 : optionsElements.length) && (optionsElements === null || optionsElements === void 0 ? void 0 : optionsElements.length) > 0
        ? optionsElements.slice(0, optionsThatFitInsideTheContainer || 1)
        : [];
    var withOverflowCounter = selectedOptions.length - (displayOptionElements || []).length;
    var allOptionsSelected = selectedOptions.length === props.options.length;
    if (!withOverflowCount) {
        return React.createElement(components.ValueContainer, __assign({}, props));
    }
    return (React.createElement(components.ValueContainer, __assign({}, __assign(__assign({}, props), { className: '' })),
        React.createElement("div", { className: 'value-container' },
            allOptionsSelected ? (React.createElement("label", { className: 'all-options-selected' }, allOptionsSelectedLabel || 'All')) : (React.createElement("div", { ref: valueContainerRef, className: 'options-container' }, displayOptionElements.length > 0
                ? displayOptionElements
                : props.selectProps.placeholder)),
            !allOptionsSelected && withOverflowCounter > 0 && (React.createElement("label", { className: 'overflow-counter' },
                "+",
                withOverflowCounter)),
            otherChildren)));
});
var Dropdown = function (props) {
    var className = props.className, optionsProp = props.options, multiple = props.multiple, _a = props.searchable, searchable = _a === void 0 ? false : _a, maxMenuHeight = props.maxMenuHeight, onChange = props.onChange, noOptionsMessage = props.noOptionsMessage, label = props.label, onSearchChange = props.onSearchChange, valueProp = props.value, disabled = props.disabled, error = props.error, indicator = props.indicator, name = props.name, onFocus = props.onFocus, onBlur = props.onBlur, withOverflowCount = props.withOverflowCount;
    var _b = React.useState(), inputTextValue = _b[0], setInputTextValue = _b[1];
    var _c = React.useState(), options = _c[0], setOptions = _c[1];
    React.useEffect(function () {
        if (optionsProp === undefined) {
            // no option selected
            setOptions([]);
            return;
        }
        if (!Array.isArray(optionsProp)) {
            if (typeof optionsProp === 'string') {
                // we have a string, set it as a unique value in the options value array
                setOptions([
                    {
                        id: optionsProp,
                        name: optionsProp,
                    },
                ]);
                return;
            }
            // we have only one option as a value, just set it in the options value array
            setOptions([optionsProp]);
            return;
        }
        if (isStringArray(optionsProp)) {
            // we have a string array
            var dropdownOptions = [];
            for (var _i = 0, optionsProp_1 = optionsProp; _i < optionsProp_1.length; _i++) {
                var option = optionsProp_1[_i];
                // map strings into options value array
                dropdownOptions.push({ id: option, name: option });
            }
            setOptions(dropdownOptions);
            // we already set the options -> just return
            return;
        }
        // we have an Options array, just set it in the options value array
        setOptions(optionsProp);
    }, [optionsProp]);
    var handleOnChange = React.useCallback(function (optionOrOptions) {
        // is necessary to prevent from keeping the search state
        // after an option is selected
        setInputTextValue(undefined);
        if (optionOrOptions === null) {
            // value came unexpectedly as null
            return;
        }
        var value_ = optionOrOptions;
        onChange === null || onChange === void 0 ? void 0 : onChange({ name: name, value: value_ });
    }, [onChange, name]);
    var classNames = ['Dropdown', 'pluggy'];
    if (className) {
        classNames.push(className);
    }
    if (valueProp) {
        classNames.push('filled');
    }
    if (multiple) {
        classNames.push('multiple');
    }
    if (error) {
        classNames.push('error');
    }
    if (disabled) {
        classNames.push('disabled');
    }
    var handleInputSearchChange = React.useCallback(function (newValue, _a) {
        var action = _a.action;
        if (action !== 'input-change') {
            // ignore any action that is not 'input-change' such as 'input-blur', etc
            // workaround https://github.com/JedWatson/react-select/issues/588#issuecomment-815133270
            return;
        }
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(newValue);
        setInputTextValue(newValue);
    }, [onSearchChange]);
    var combinedClassNames = classNames.join(' ');
    // map value prop, which can be 'string | string[] | Option | Option[] | undefined' to lib-needed 'Option | Option[] | null'
    var valuePropMapped = React.useMemo(function () {
        if (typeof valueProp === 'undefined') {
            return null;
        }
        if (typeof valueProp === 'string') {
            return { id: valueProp, name: valueProp };
        }
        if (Array.isArray(valueProp) && isStringArray(valueProp)) {
            return valueProp.map(function (value_) { return ({ id: value_, name: value_ }); });
        }
        return valueProp;
    }, [valueProp]);
    var enabledOptions = options === null || options === void 0 ? void 0 : options.filter(function (option) { return !option.isDisabled; });
    var handleSelectAllOptions = React.useCallback(function () {
        if (enabledOptions === undefined) {
            return;
        }
        handleOnChange(enabledOptions);
    }, [handleOnChange, options]);
    var handleUnselectAllOptions = React.useCallback(function () {
        handleOnChange([]);
    }, [handleOnChange]);
    var allOptionsSelected = React.useMemo(function () {
        if (enabledOptions === undefined ||
            valuePropMapped === null ||
            !Array.isArray(valuePropMapped)) {
            return false;
        }
        return valuePropMapped.length === enabledOptions.length;
    }, [enabledOptions, valuePropMapped]);
    return (React.createElement("div", { className: combinedClassNames },
        React.createElement(SelectWithCustomProps, { allOptionsSelectedLabel: props.multiple ? props.allOptionsSelectedLabel : '', 
            // we are setting an inner className inside all the components in the Dropdown
            // we have to keep in mind that this ensures compatibility with styled components,
            // CSS modules and other libraries. Maybe we can iterate this in the future to use the
            // style API from react-select https://react-select.com/styles#styles
            classNamePrefix: 'dropdown', 
            // if the dropdown is multiselect don't close the menu when a item is selected
            closeMenuOnSelect: !multiple, components: {
                DropdownIndicator: indicator === null
                    ? function () { return null; }
                    : // type casting needed to avoid weird Webpack error complaining, although types should match
                        indicator || DropdownIndicator,
                IndicatorSeparator: function () { return null; },
                MultiValueRemove: MultiValueRemove,
                IndicatorsContainer: IndicatorsContainer,
                Option: props.multiple ? OptionMultiple : OptionSingle,
                ValueContainer: ValueContainer,
                MultiValueLabel: MultiValueLabel,
                MenuList: props.multiple && props.withSelectAllOption
                    ? function (menuListProps) { return (React.createElement(MenuListWithSelectAllOption, __assign({}, __assign(__assign({}, menuListProps), { selectAllLabel: props.selectAllLabel, allOptionsSelected: allOptionsSelected, onSelectAllOptions: handleSelectAllOptions, onUnselectAllOptions: handleUnselectAllOptions, customChildren: props.children })))); }
                    : function (menuListProps) { return (React.createElement(MenuList, __assign({}, __assign(__assign({}, menuListProps), { customChildren: props.children })))); },
            }, isDisabled: disabled, 
            // current input value
            inputValue: inputTextValue, 
            // don't hide an option when a user selects it
            hideSelectedOptions: false, 
            // on change handler
            onChange: handleOnChange, onFocus: onFocus, onBlur: onBlur, isMulti: multiple, options: options, value: valuePropMapped, 
            // input value change event handler
            onInputChange: handleInputSearchChange, 
            // property to allow the user to search with text inside the dropdown
            isSearchable: searchable, placeholder: React.createElement("div", { className: 'placeholder' }, label || ''), isClearable: false, 
            // filters to improve user's search, such as ignoring accents
            filterOption: createFilter(filterConfig), 
            // max height of the menu
            maxMenuHeight: maxMenuHeight, 
            // message to show when the user doesn't have options to select
            noOptionsMessage: noOptionsMessage, 
            // necessary to match search with connectors name
            getOptionLabel: function (option) { return String(option.name); }, 
            // necessary to match the value of the internal dropdown with the options id
            getOptionValue: function (option) { return String(option.id); }, isOptionDisabled: function (option) {
                return Boolean(option.isDisabled);
            }, withOverflowCount: withOverflowCount })));
};
export { Dropdown };
